<script setup>
// M26.1 — presentational wrapper shared by every Project Knowledge card.
// UI only: save status / collapse / anchor / last-updated display, no data logic.
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  id: { type: String, required: true },
  icon: { type: String, required: true },
  title: { type: String, required: true },
  status: { type: String, default: 'idle' }, // idle | pending | saving | saved | error
  lastSavedAt: { type: Date, default: null },
  collapsible: { type: Boolean, default: false },
  // Controlled by the parent (v-model:collapsed) rather than owned locally —
  // adding an item to a collapsed section must be able to force it open so
  // the new row (and its remove button) is immediately visible.
  collapsed: { type: Boolean, default: false },
  itemCount: { type: Number, default: null },
});

const emit = defineEmits(['update:collapsed']);

function toggleCollapsed() {
  emit('update:collapsed', !props.collapsed);
}

const STATUS_LABELS = {
  pending: 'Unsaved…',
  saving: 'Saving…',
  saved: 'Saved',
  error: 'Save failed',
};

const statusLabel = computed(() => STATUS_LABELS[props.status] || '');

const statusClass = computed(
  () =>
    ({
      pending: 'text-n-slate-7',
      saving: 'text-woot-500',
      saved: 'text-success-600 dark:text-success-400',
      error: 'text-ruby-600 dark:text-ruby-400',
    })[props.status] || ''
);

// Keeps the "Updated Xm ago" badge fresh without a manual refresh.
const now = ref(Date.now());
let clockTimer = null;
onMounted(() => {
  clockTimer = setInterval(() => {
    now.value = Date.now();
  }, 30_000);
});
onBeforeUnmount(() => clearInterval(clockTimer));

const relativeTime = computed(() => {
  if (!props.lastSavedAt) return '';
  const sec = Math.round((now.value - props.lastSavedAt.getTime()) / 1000);
  if (sec < 10) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    :id="id"
    tabindex="-1"
    class="rounded-xl border border-n-weak bg-n-background p-4 flex flex-col h-full scroll-mt-32 focus:outline-none focus-visible:ring-2 focus-visible:ring-woot-500/40"
  >
    <div class="flex items-center justify-between gap-2 mb-2">
      <button
        v-if="collapsible"
        type="button"
        class="flex items-center gap-1.5 text-sm font-semibold text-n-slate-12 min-w-0 flex-1 text-left"
        :aria-expanded="!collapsed"
        @click="toggleCollapsed"
      >
        <span class="size-3.5 text-woot-500 shrink-0" :class="icon" />
        <span class="truncate">{{ title }}</span>
        <span
          v-if="itemCount !== null"
          class="text-[11px] font-normal text-n-slate-7 shrink-0"
        >
          ({{ itemCount }})
        </span>
        <span
          class="i-lucide-chevron-down size-3.5 text-n-slate-6 shrink-0 transition-transform"
          :class="{ '-rotate-90': collapsed }"
        />
      </button>
      <h3
        v-else
        class="text-sm font-semibold text-n-slate-12 flex items-center gap-1.5 min-w-0"
      >
        <span class="size-3.5 text-woot-500 shrink-0" :class="icon" />
        <span class="truncate">{{ title }}</span>
      </h3>

      <div class="flex items-center gap-2 shrink-0">
        <Transition name="save-status" mode="out-in">
          <span
            v-if="statusLabel"
            :key="status"
            class="flex items-center gap-1 text-[11px] font-medium"
            :class="statusClass"
          >
            <span
              v-if="status === 'saving'"
              class="i-lucide-loader-2 size-3 animate-spin"
            />
            <span
              v-else-if="status === 'saved'"
              class="i-lucide-check-circle-2 size-3"
            />
            <span
              v-else-if="status === 'error'"
              class="i-lucide-alert-circle size-3"
            />
            {{ statusLabel }}
          </span>
          <span
            v-else-if="lastSavedAt"
            key="idle"
            class="text-[11px] text-n-slate-6"
            :title="lastSavedAt.toLocaleString()"
          >
            Updated {{ relativeTime }}
          </span>
        </Transition>
        <slot name="actions" />
      </div>
    </div>

    <div v-show="!collapsed" class="flex-1 flex flex-col gap-2 min-h-0">
      <slot />
    </div>
  </section>
</template>

<style scoped>
.save-status-enter-active,
.save-status-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.save-status-enter-from {
  opacity: 0;
  transform: translateY(-2px);
}
.save-status-leave-to {
  opacity: 0;
  transform: translateY(2px);
}
</style>
